import { useRef, useState } from "react";
import { Bold, Italic, Heading2, Heading3, List, ListOrdered, Link2, Image as ImageIcon, Eraser, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "../lib/api";

const Btn = ({ onDo, title, testid, children }) => (
  <button
    type="button"
    title={title}
    data-testid={testid}
    onMouseDown={(e) => { e.preventDefault(); onDo(); }}
    className="h-8 w-8 flex items-center justify-center rounded-md text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
  >
    {children}
  </button>
);

export const RichTextEditor = ({ value, onChange, testid = "rich-editor" }) => {
  const ref = useRef(null);
  const fileRef = useRef(null);
  const initialized = useRef(false);
  const [uploading, setUploading] = useState(false);

  if (ref.current && !initialized.current) {
    ref.current.innerHTML = value || "";
    initialized.current = true;
  }

  const emit = () => onChange(ref.current ? ref.current.innerHTML : "");
  const exec = (cmd, val = null) => { document.execCommand(cmd, false, val); ref.current?.focus(); emit(); };

  const addLink = () => {
    const url = window.prompt("أدخل الرابط (https://...)");
    if (url) exec("createLink", url);
  };

  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const res = await api.uploadPageImage(file);
      const src = fileUrl(res.file_id);
      ref.current?.focus();
      document.execCommand("insertHTML", false, `<img src="${src}" style="max-width:100%;border-radius:12px;margin:12px 0;" />`);
      emit();
      toast.success("تم إدراج الصورة");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "فشل رفع الصورة");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden bg-white" data-testid={testid}>
      <div className="flex items-center gap-0.5 border-b border-slate-100 bg-slate-50 px-1.5 py-1 flex-wrap">
        <Btn title="عريض" testid={`${testid}-bold`} onDo={() => exec("bold")}><Bold className="h-4 w-4" /></Btn>
        <Btn title="مائل" testid={`${testid}-italic`} onDo={() => exec("italic")}><Italic className="h-4 w-4" /></Btn>
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <Btn title="عنوان" testid={`${testid}-h2`} onDo={() => exec("formatBlock", "H2")}><Heading2 className="h-4 w-4" /></Btn>
        <Btn title="عنوان فرعي" testid={`${testid}-h3`} onDo={() => exec("formatBlock", "H3")}><Heading3 className="h-4 w-4" /></Btn>
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <Btn title="قائمة نقطية" testid={`${testid}-ul`} onDo={() => exec("insertUnorderedList")}><List className="h-4 w-4" /></Btn>
        <Btn title="قائمة مرقّمة" testid={`${testid}-ol`} onDo={() => exec("insertOrderedList")}><ListOrdered className="h-4 w-4" /></Btn>
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <Btn title="رابط" testid={`${testid}-link`} onDo={addLink}><Link2 className="h-4 w-4" /></Btn>
        <Btn title="صورة" testid={`${testid}-image`} onDo={() => fileRef.current?.click()}>
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
        </Btn>
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <Btn title="إزالة التنسيق" testid={`${testid}-clear`} onDo={() => exec("removeFormat")}><Eraser className="h-4 w-4" /></Btn>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickImage} data-testid={`${testid}-file`} />
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        dir="rtl"
        onInput={emit}
        data-testid={`${testid}-area`}
        className="rte-content min-h-[160px] max-h-[360px] overflow-y-auto px-3 py-2.5 text-sm leading-relaxed text-slate-700 focus:outline-none"
      />
    </div>
  );
};

export default RichTextEditor;
